import { useTrackingStore } from 'store/trackingStore';
import { useAppStore } from 'store/appStore';
import { useAuthStore } from 'store/authStore';
import { useReceiptStore } from 'store/receiptStore';
import { SCREEN_TYPES } from 'utils/constants';
import { startGrading } from 'services/api/product';
import { toast } from 'sonner';
import { playErrorSound, playGoodSound } from 'utils/sounds';
import { ReceiptData } from 'types';
import { useProductStore } from '../store';

interface ProcessTrackingOptions {
  trackingId: string;
  source?: 'fedex' | 'manual' | 'scanner';
  onLoading?: (loading: boolean) => void;
  trackingInputRef?: React.RefObject<HTMLInputElement>;
  onProblem?: (data: ReceiptData) => void;
}

export const processTrackingId = async ({
  trackingId,
  source = 'manual',
  onLoading,
  trackingInputRef,
  onProblem,
}: ProcessTrackingOptions) => {
  const {
    lastTrackingId,
    setLastTrackingId,
    lastRmaId,
    setLastRmaId,
    setPendingTrackingId,
    setCurrentTrackingId,
  } = useTrackingStore.getState();
  const { setCurrentScreen } = useAppStore.getState();
  const { setReceiptData } = useReceiptStore.getState();
  const { user, stationId } = useAuthStore.getState();
  const { setCurrentProduct, setProductId } = useProductStore.getState();

  const trimmedTrackingId = trackingId.trim();

  const onBoxScanned = (data: ReceiptData): void => {
    setReceiptData(data);
    setLastTrackingId(data.tracking_id || data.receipt?.tracking_id || null);
    setLastRmaId(data.rma_id || data.receipt?.rma_id || null);
    setCurrentScreen(SCREEN_TYPES.SCAN_ITEM);
  };

  if (lastTrackingId && trimmedTrackingId !== lastTrackingId && trimmedTrackingId !== lastRmaId) {
    setPendingTrackingId(trimmedTrackingId);
    setCurrentScreen(SCREEN_TYPES.CONFIRM_CLOSE);
    return;
  }

  onLoading?.(true);

  const scanningMessage =
    source === 'fedex' ? 'Scanning FedEx tracking number...' : 'Scanning tracking number...';
  toast(scanningMessage, { icon: '🔍' });

  try {
    const gradingResult = await startGrading(trimmedTrackingId, user.id, stationId);

    if (gradingResult?.responseState === 'confirmed') {
      const successMessage =
        source === 'fedex'
          ? '✓ FedEx tracking found! Loading items...'
          : '✓ Tracking found! Loading items...';
      playGoodSound();
      toast.success(successMessage);
      onBoxScanned(gradingResult as ReceiptData);
      setCurrentProduct(null); // Clear any previously selected product
      setProductId('');
    } else if (gradingResult?.responseState === 'problem' && onProblem) {
      playErrorSound();
      onLoading?.(false);
      setCurrentTrackingId('');
      onProblem(gradingResult as ReceiptData);
    } else {
      // Show error message and stay on the same page
      const errorMessage = gradingResult?.responseMsg || 'Tracking number not found';
      playErrorSound();
      toast.error(errorMessage);
      onLoading?.(false);
      setCurrentTrackingId('');
      requestAnimationFrame(() => trackingInputRef?.current?.focus());
    }
  } catch (err) {
    playErrorSound();
    toast.error('✗ Failed to connect to server');
    onLoading?.(false);
  }
};
